import { isPresent } from '@ais-ui/utils';
import {
  AutoCompleteShipInfo,
  Filter,
  Group,
  MakePermissions,
  MappedPermissions,
  ReportTemplate,
} from '@ais-ui/websockets';
import { ReportType } from './reportsTypes';
import { GeoJson } from '@ais-ui/api-spec/shapes';

export type GeoJsonDictionary = {
  [key: string]: GeoJson;
};

export type StringDictionary = {
  [key: string]: string;
};

export type PortDictionary = {
  [key: string]: string[];
};

export type WorldDictionary = {
  [key: string]: boolean;
};

export type NumberDictionary = {
  [key: string]: number;
};

export type ShapeDictionaryType =
  | GeoJsonDictionary
  | PortDictionary
  | WorldDictionary
  | StringDictionary
  | NumberDictionary
  | null;

export type ReportFilterType =
  | Filter
  | AutoCompleteShipInfo[]
  | string
  | Record<string, Filter | AutoCompleteShipInfo[] | string | null>;

export const getGroupsFromIds = (
  reportTemplate: ReportTemplate,
  groups: Group[]
): Group[] => {
  const reportTemplateGroups = reportTemplate.groups || [];

  return (
    groups?.filter(
      (group) =>
        reportTemplateGroups.includes(group.uuid) ||
        reportTemplateGroups.includes(group.name)
    ) || []
  );
};
const filterReportTemplateByNameOrJobName = (
  reportTemplate: ReportTemplate,
  name: string
): ReportTemplate | null => {
  const lowercaseName = name.toLowerCase();
  // Check if template name matches
  if (reportTemplate.tplName.toLowerCase().includes(lowercaseName)) {
    return reportTemplate;
  }

  // Check if type matches
  if (reportTemplate.type.toLowerCase().includes(lowercaseName)) {
    return reportTemplate;
  }

  // Check if any job name or id matches
  for (const job of reportTemplate.jobs) {
    if (job.name.toLowerCase().includes(lowercaseName)) {
      return reportTemplate;
    }
    if (job.uuid.toLowerCase().includes(lowercaseName)) {
      return reportTemplate;
    }
  }

  return null;
};

export const filterReportTemplates = (
  reportTemplates: ReportTemplate[],
  name: string
) => {
  const lowercaseName = name.toLowerCase();
  return reportTemplates
    .map((reportTemplate) =>
      filterReportTemplateByNameOrJobName(reportTemplate, lowercaseName)
    )
    .filter(isPresent);
};

const filterReportTypeByLabel = (
  reportType: ReportType,
  label: string
): ReportType | null => {
  if (reportType.label.toLowerCase().includes(label)) {
    return reportType;
  }

  return null;
};

export const filterReportTypes = (
  reportTypes: ReportType[],
  label: string,
  permissions: MakePermissions<MappedPermissions> | null
) => {
  const lowercaseLabel = label.toLowerCase();

  return reportTypes
    .filter((reportType) => !reportType.disabled)
    .filter(
      (reportType) =>
        permissions &&
        reportType.type in permissions &&
        permissions[reportType.type]
    ) // First check if the permission exists, then check if it's not false
    .map((reportType) => filterReportTypeByLabel(reportType, lowercaseLabel))
    .filter(isPresent);
};

const isAutoCompleteShipInfoArray = (
  filter: any
): filter is AutoCompleteShipInfo[] => {
  return (
    Array.isArray(filter) &&
    filter.every(
      (item) =>
        Object.prototype.hasOwnProperty.call(item, 'callSign') &&
        Object.prototype.hasOwnProperty.call(item, 'imo') &&
        Object.prototype.hasOwnProperty.call(item, 'mmsi') &&
        Object.prototype.hasOwnProperty.call(item, 'name') &&
        Object.prototype.hasOwnProperty.call(item, 'type')
    )
  );
};

export const translateFilterToBackend = (
  filter: Filter | AutoCompleteShipInfo[] | string | null
) => {
  let ships: Array<{
    name: string | null;
    mmsi: string | null;
    callsign: string | null;
    imo: string | null;
  }> = [];
  let normalFilter: Filter | null = null;
  let shiplistFilter: string | null = null;

  if (isAutoCompleteShipInfoArray(filter)) {
    ships = filter.map((ship) => ({
      name: ship.name,
      mmsi: ship.mmsi,
      callsign: ship.callSign,
      imo: ship.imo,
    }));
  } else if (typeof filter === 'string') {
    shiplistFilter = filter as string;
  } else if (filter) {
    normalFilter = filter as Filter;
  }

  return {
    ships,
    normalFilter,
    shiplistFilter,
  };
};
